import { useMemo, useRef, useState } from 'react'
import { attachmentsOf } from '../../../shared/attachments.js'
import { parseQuestion } from '../../../shared/mentions.js'
import { copyText } from '../../lib/clipboard.js'
import { buildAnswer, answerToText } from './buildAnswer.js'
import { AssistantMessage, ErrorNotice, StoppedNote, UserMessage } from './Messages.jsx'
import { WidgetContext } from './widgets/index.js'

function textOf(message) {
  return (message.parts ?? [])
    .filter((p) => p.type === 'text')
    .map((p) => p.content ?? '')
    .join('')
}

/** A user message as plain words (mentions as their names), for a widget's answered line. */
function replyText(message) {
  const text = textOf(message).trim()
  if (!text) return null
  const plain = parseQuestion(text)
    .segments.map((seg) => (seg.type === 'mention' ? seg.label : seg.value))
    .join('')
    .trim()
  return plain.length > 140 ? `${plain.slice(0, 139)}…` : plain
}

const EMPTY_ANSWER = { meta: null, blocks: [], sources: null, activity: [], followups: [], writing: false }

/**
 * The conversation: user bubbles and answers.
 *
 * - `error` is the live error of the newest answer (an Error or ApiError);
 *   `stopped` means the user pressed Stop on it.
 * - `live` is true once this view has sent something. Until then the newest
 *   turn's stored outcome (error, stopped) is shown, as for older turns.
 * - `widgetStates` / `onSaveWidget` back the stateful widgets; without
 *   onSaveWidget they are read-only.
 */
export function Thread({
  messages,
  turns = [],
  systemsMap,
  streaming = false,
  error = null,
  stopped = false,
  live = false,
  onRetry,
  feedback = {},
  onRate,
  onAsk,
  widgetStates = {},
  onSaveWidget,
  readOnly = false,
}) {
  const [copiedId, setCopiedId] = useState(null)
  const turnsById = useMemo(() => new Map(turns.map((t) => [t.id, t])), [turns])
  const widgetContext = useMemo(
    () => ({
      states: widgetStates ?? {},
      save: readOnly ? null : (onSaveWidget ?? null),
      readOnly: readOnly || !onSaveWidget,
      onAsk: readOnly ? null : (onAsk ?? null),
      systemsMap,
    }),
    [widgetStates, onSaveWidget, readOnly, onAsk, systemsMap],
  )

  // When the newest answer started streaming, for its elapsed-time counter.
  const startedAt = useRef(null)
  if (streaming && startedAt.current === null) startedAt.current = Date.now()
  if (!streaming) startedAt.current = null

  const items = []
  let turn = null
  const lastIndex = messages.length - 1
  const storedError = (t) => (t?.status === 'error' ? t.error || 'This answer did not finish.' : null)
  const storedStop = (t) => t?.status === 'aborted'

  messages.forEach((message, index) => {
    if (message.role === 'user') {
      const text = textOf(message)
      turn = turnsById.get(message.id) ?? null
      items.push(<UserMessage key={message.id} text={text} attachments={attachmentsOf(message)} systemsMap={systemsMap} />)
      // No answer arrived: the question was rejected, failed, or was stopped.
      if (index === lastIndex && !streaming) {
        const failed = error ?? (live ? null : storedError(turn))
        if (failed) {
          items.push(
            <ErrorNotice key={`${message.id}-error`} error={failed} onRetry={onRetry} readOnly={readOnly} />,
          )
        } else if (stopped || (!live && storedStop(turn))) {
          items.push(<StoppedNote key={`${message.id}-stopped`} onRetry={onRetry} readOnly={readOnly} />)
        }
      }
      return
    }
    if (message.role !== 'assistant') return
    const answer = buildAnswer(message)
    const isLast = index === lastIndex
    const nextUser = messages.slice(index + 1).find((m) => m.role === 'user')
    const shownError = isLast ? (error ?? (live ? null : storedError(turn))) : storedError(turn)
    const shownStop = isLast ? stopped || (!live && storedStop(turn)) : storedStop(turn)
    items.push(
      <AssistantMessage
        key={message.id}
        answer={answer}
        systemsMap={systemsMap}
        streaming={streaming && isLast}
        error={shownError}
        stopped={shownStop}
        startedAt={isLast ? startedAt.current : null}
        onRetry={isLast ? onRetry : undefined}
        feedback={feedback[message.id] ?? 0}
        onRate={(rating) => onRate?.(message.id, rating)}
        copied={copiedId === message.id}
        onCopy={async () => {
          if (await copyText(answerToText(answer, widgetStates))) {
            setCopiedId(message.id)
            setTimeout(() => setCopiedId((id) => (id === message.id ? null : id)), 1600)
          }
        }}
        onAsk={isLast ? onAsk : undefined}
        reply={nextUser ? replyText(nextUser) : null}
        readOnly={readOnly}
      />,
    )
  })

  // Waiting for the first byte of a new answer.
  if (streaming && messages[lastIndex]?.role === 'user') {
    items.push(
      <AssistantMessage
        key="pending-answer"
        answer={EMPTY_ANSWER}
        systemsMap={systemsMap}
        streaming
        startedAt={startedAt.current}
        readOnly
      />,
    )
  }

  return <WidgetContext.Provider value={widgetContext}>{items}</WidgetContext.Provider>
}
